import type { ViewTransform } from '@/state/store'

/** Convert a screen (px) point to world (feet) coordinates given the view transform. */
export function screenToWorld(px: number, py: number, view: ViewTransform) {
  return { x: (px - view.x) / view.scale, y: (py - view.y) / view.scale }
}

/** World (feet) -> screen (px). */
export function worldToScreen(x: number, y: number, view: ViewTransform) {
  return { x: x * view.scale + view.x, y: y * view.scale + view.y }
}

/** Visible world rectangle (feet) for a stage of the given pixel size. */
export function visibleWorldBounds(view: ViewTransform, widthPx: number, heightPx: number) {
  const tl = screenToWorld(0, 0, view)
  const br = screenToWorld(widthPx, heightPx, view)
  return { minX: tl.x, minY: tl.y, maxX: br.x, maxY: br.y }
}

const NICE_STEPS = [0.25, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500]

/**
 * Pick a grid step (feet) whose on-screen spacing is at least `minPx`, so the
 * grid never becomes an unreadable mush when zoomed out.
 */
export function niceGridStep(view: ViewTransform, minPx = 9): number {
  for (const step of NICE_STEPS) {
    if (step * view.scale >= minPx) return step
  }
  return NICE_STEPS[NICE_STEPS.length - 1]
}

const MIN_SCALE = 1.5 // px per foot
const MAX_SCALE = 120

/** Zoom keeping the world point under the cursor fixed. */
export function zoomAtPoint(
  view: ViewTransform,
  pointerPx: { x: number; y: number },
  factor: number,
): ViewTransform {
  const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.scale * factor))
  const world = screenToWorld(pointerPx.x, pointerPx.y, view)
  return {
    scale: newScale,
    x: pointerPx.x - world.x * newScale,
    y: pointerPx.y - world.y * newScale,
  }
}
